import React, { useEffect, useMemo, useState } from 'react';
import { Container, Box, Button } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'react-simple-snackbar';
import styles from '../Clients/Clients.module.css';
import AddLocation from './AddLocation';
import AddContact from '../Contacts/AddContact';
import { getLocationsByUser, deleteLocation } from '../../actions/locationActions';
import { getAccountsByUser } from '../../actions/accountActions';
import { getContactsByUser, deleteContact } from '../../actions/contactActions';
import useAuthUser from '../../hooks/useAuthUser';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';

const Locations = () => {
  const auth = useAuthUser()
  const dispatch = useDispatch()
  const { locations, isLoading } = useSelector((state) => state.locations)
  const accounts = useSelector((state) => state.accounts.accounts || [])
  const contacts = useSelector((state) => state.contacts.contacts || [])
  const [locationModalOpen, setLocationModalOpen] = useState(false)
  const [currentId, setCurrentId] = useState(null)
  const [contactsPanelOpen, setContactsPanelOpen] = useState(false)
  const [contactsLocation, setContactsLocation] = useState(null)
  const [contactCurrentId, setContactCurrentId] = useState(null)
  const [contactFormOpen, setContactFormOpen] = useState(false)
  const [mobileDetailLocation, setMobileDetailLocation] = useState(null)
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false)
  const isMobile = useMediaQuery('(max-width:600px)')
  // eslint-disable-next-line no-unused-vars
  const [openSnackbar, closeSnackbar] = useSnackbar()

  const contactBelongsToLocation = (contact, locationId) => {
    if (!contact || !locationId) return false
    const ids = Array.isArray(contact.locationIds) && contact.locationIds.length
      ? contact.locationIds
      : (contact.locationId ? [contact.locationId] : [])
    return ids.some((id) => String(id) === String(locationId))
  }

  useEffect(() => {
    const userId = auth?.result?._id || auth?.result?.googleId
    if (userId) {
      dispatch(getLocationsByUser({ search: userId }))
      if (!accounts.length) {
        dispatch(getAccountsByUser({ search: userId }))
      }
      dispatch(getContactsByUser({ search: userId }))
    }
  }, [auth, dispatch])

  const accountNameById = useMemo(() => {
    const map = {}
    accounts.forEach(a => { map[a._id] = a.name })
    return map
  }, [accounts])

  const rows = useMemo(() => {
    return [...(locations || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
  }, [locations])

  const contactsForSelectedLocation = useMemo(() => {
    if (!contactsLocation) return []
    return contacts.filter((c) => contactBelongsToLocation(c, contactsLocation._id))
  }, [contacts, contactsLocation])

  const handleEdit = (id) => {
    setCurrentId(id)
    setLocationModalOpen(true)
  }

  const handleDelete = (id) => {
    dispatch(deleteLocation(id, openSnackbar))
  }

  const handleOpenContacts = (location) => {
    setContactsLocation(location)
    setContactsPanelOpen(true)
    setContactCurrentId(null)
  }

  const handleDeleteContact = (id) => {
    dispatch(deleteContact(id, openSnackbar))
  }

  const openMobileDetail = (location) => {
    setMobileDetailLocation(location)
    if (mobileDetailOpen) return
    window.requestAnimationFrame(() => setMobileDetailOpen(true))
  }

  const closeMobileDetail = () => {
    setMobileDetailOpen(false)
  }

  const handleMobileDetailTransitionEnd = (event) => {
    if (event.target !== event.currentTarget) return
    if (!mobileDetailOpen) setMobileDetailLocation(null)
  }

  const columns = [
    { field: 'name', headerName: 'Name', flex: 1.2, minWidth: 180 },
    {
      field: 'accountId',
      headerName: 'Parent Account',
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => accountNameById[params.value] || '',
    },
    { field: 'shippingAddress', headerName: 'Shipping Address', flex: 1.2, minWidth: 180 },
    { field: 'shippingCity', headerName: 'City', flex: 0.8, minWidth: 130 },
    { field: 'shippingState', headerName: 'State', flex: 0.4, minWidth: 90 },
    { field: 'shippingZip', headerName: 'Zip', flex: 0.5, minWidth: 100 },
    { field: 'phone', headerName: 'Phone', flex: 0.9, minWidth: 140 },
    {
      field: 'active',
      headerName: 'Active',
      flex: 0.4,
      minWidth: 90,
      renderCell: (params) => (
        <span style={{ color: params.value ? 'green' : 'gray', fontWeight: 600 }}>
          {params.value ? 'Yes' : 'No'}
        </span>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      width: 260,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Button
            style={{color: 'white', backgroundColor: 'rgb(25, 118, 210)', marginRight: 10}}
            onClick={(e) => { e.stopPropagation(); handleEdit(params.row._id); }}
            variant="contained"
            size="small"
          >
            Edit
          </Button>
          <Button
            style={{color: 'white', backgroundColor: 'red'}}
            onClick={(e) => { e.stopPropagation(); handleDelete(params.row._id); }}
            variant="contained"
            size="small"
          >
            Delete
          </Button>
          <Button
            style={{color: 'white', backgroundColor: '#555', marginLeft: 4}}
            onClick={(e) => { e.stopPropagation(); handleOpenContacts(params.row); }}
            variant="contained"
            size="small"
          >
            Contacts
          </Button>
        </Box>
      ),
    },
  ]

  const mobileColumns = [
    { field: 'name', headerName: 'Name', flex: 1.1, minWidth: 150 },
    {
      field: 'accountId',
      headerName: 'Parent Account',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => accountNameById[params.value] || '',
    },
  ]

  return (
    <div className={styles.pageLayout}>
      <Container style={{ width: '90%', maxWidth: 1400 }}>
        <div className={styles.buttons}>
          <h1>Locations</h1>
          <button
            className={styles.btn}
            onClick={() => { setCurrentId(null); setLocationModalOpen((prev) => !prev) }}
          >
            Add Location
          </button>
        </div>
        {isMobile ? (
          <div className={styles.mobileGridWrap}>
            <div className={styles.mobileResultMeta}>
              <span>Tap a row for details</span>
            </div>
            <DataGrid
              autoHeight
              loading={isLoading}
              rows={rows}
              getRowId={(row) => row._id}
              columns={mobileColumns}
              pageSize={25}
              rowsPerPageOptions={[10, 25, 50, 100]}
              components={{ Toolbar: GridToolbar }}
              componentsProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 } } }}
              sx={{
                border: '1px solid #dbe3ef',
                borderRadius: '12px',
                backgroundColor: '#fff',
                '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8fafc', minHeight: '42px !important' },
                '& .MuiDataGrid-columnHeaderTitle': { fontSize: '12px', fontWeight: 800 },
                '& .MuiDataGrid-cell': { py: '8px', fontSize: '13px', whiteSpace: 'normal', lineHeight: 1.25 },
                '& .MuiDataGrid-row': { cursor: 'pointer' },
                '& .MuiDataGrid-toolbarContainer': { flexWrap: 'wrap', gap: '4px', p: '6px' },
                '& .MuiTablePagination-toolbar': { px: '8px' },
              }}
              onRowClick={(params) => openMobileDetail(params.row)}
            />
          </div>
        ) : (
          <DataGrid
            autoHeight
            loading={isLoading}
            rows={rows}
            getRowId={(row) => row._id}
            columns={columns}
            pageSize={25}
            rowsPerPageOptions={[10, 25, 50, 100]}
            components={{ Toolbar: GridToolbar }}
            componentsProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 } } }}
            sx={{
              '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '6px' },
              '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '8px' },
              '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '12px' },
            }}
            onRowDoubleClick={(params) => handleEdit(params.id)}
          />
        )}
        {mobileDetailLocation && (
          <aside
            className={`${styles.mobileDetailSidenav} ${mobileDetailOpen ? styles.mobileDetailSidenavOpen : ''}`}
            aria-label="Location details"
            onTransitionEnd={handleMobileDetailTransitionEnd}
          >
            <div className={styles.mobileDetailContent}>
              <div className={styles.mobileDetailTopBar}>
                <span>Location details</span>
                <Button size="small" color="primary" onClick={closeMobileDetail}>Close</Button>
              </div>
              <div className={styles.mobileDetailHeader}>
                <div>
                  <h2>{mobileDetailLocation.name || 'Location'}</h2>
                  <p>{accountNameById[mobileDetailLocation.accountId] || 'No parent account'}</p>
                </div>
              </div>
              <div className={styles.mobileDetailGrid}>
                <div><span>Address</span><strong>{mobileDetailLocation.shippingAddress || '—'}</strong></div>
                <div><span>City</span><strong>{mobileDetailLocation.shippingCity || '—'}</strong></div>
                <div><span>State</span><strong>{mobileDetailLocation.shippingState || '—'}</strong></div>
                <div><span>Zip</span><strong>{mobileDetailLocation.shippingZip || '—'}</strong></div>
                <div><span>Phone</span><strong>{mobileDetailLocation.phone || '—'}</strong></div>
                <div><span>Active</span><strong>{mobileDetailLocation.active ? 'Yes' : 'No'}</strong></div>
                <div><span>Contacts</span><strong>{contacts.filter((c) => contactBelongsToLocation(c, mobileDetailLocation._id)).length}</strong></div>
              </div>
              <div className={styles.mobileDrawerActions}>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    handleEdit(mobileDetailLocation._id)
                    closeMobileDetail()
                  }}
                >
                  Edit
                </Button>
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={() => {
                    handleOpenContacts(mobileDetailLocation)
                    closeMobileDetail()
                  }}
                >
                  Contacts
                </Button>
                <Button
                  color="secondary"
                  variant="outlined"
                  onClick={() => {
                    handleDelete(mobileDetailLocation._id)
                    closeMobileDetail()
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          </aside>
        )}
      </Container>
      <AddLocation open={locationModalOpen} setOpen={setLocationModalOpen} currentId={currentId} setCurrentId={setCurrentId} />
      <Dialog open={contactsPanelOpen} onClose={() => { setContactsPanelOpen(false); setContactCurrentId(null); setContactsLocation(null); setContactFormOpen(false) }} fullWidth maxWidth="sm">
        <DialogTitle>
          {contactsLocation ? `Contacts for ${contactsLocation.name || 'Location'}` : 'Contacts'}
        </DialogTitle>
        <DialogContent dividers>
          {contactsForSelectedLocation.length === 0 ? (
            <p style={{ color: '#555' }}>No contacts for this location.</p>
          ) : (
            contactsForSelectedLocation.map((c) => (
              <Box key={c._id} display="flex" alignItems="center" justifyContent="space-between" style={{ padding: '6px 0' }}>
                <div>
                  <strong>{c.name}</strong>{c.primary ? ' • Primary' : ''}<br />
                  <span style={{ color: '#555' }}>{c.role || ''}</span><br />
                  <span style={{ color: '#555' }}>{c.email || ''} {c.phone ? `• ${c.phone}` : ''}</span>
                </div>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <Button
                    style={{color: 'white', backgroundColor: 'rgb(25, 118, 210)'}}
                    variant="contained"
                    size="small"
                    onClick={() => { setContactCurrentId(c._id); setContactFormOpen(true); }}
                  >
                    Edit
                  </Button>
                  <Button
                    style={{color: 'white', backgroundColor: 'red'}}
                    variant="contained"
                    size="small"
                    onClick={() => handleDeleteContact(c._id)}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setContactCurrentId(null); setContactFormOpen(true); }} color="primary" variant="contained">
            Add Contact
          </Button>
          <Button onClick={() => { setContactsPanelOpen(false); setContactCurrentId(null); setContactsLocation(null); setContactFormOpen(false) }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <AddContact
        open={contactFormOpen}
        setOpen={(val) => {
          if (!val) {
            setContactCurrentId(null)
            setContactFormOpen(false)
          } else {
            setContactFormOpen(true)
          }
        }}
        currentId={contactCurrentId}
        setCurrentId={setContactCurrentId}
        defaultAccountId={contactsLocation?.accountId}
        defaultLocationId={contactsLocation?._id}
      />
    </div>
  );
};

export default Locations;
